import { AuthApi, Configuration } from '@linktivity/ars-neo';
import { getStorageItem, setStorageItem } from '~/utils/storage';

const ARS_API_BASE_URL = import.meta.env.VITE_ARS_API_BASE_URL as string;

// Tokens in local storage (namespaced by ~/utils/storage) and in the URL
// query parameters of shared links, under the same names.
const STORAGE_KEYS = ['accessToken', 'refreshToken'] as const;

type StorageKey = (typeof STORAGE_KEYS)[number];

function read(key: StorageKey): string {
  return getStorageItem(key) ?? '';
}

function write(key: StorageKey, value: string): void {
  setStorageItem(key, value);
}

function captureSessionFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let captured = false;

  STORAGE_KEYS.forEach(key => {
    const value = url.searchParams.get(key);
    if (value) {
      write(key, value);
      url.searchParams.delete(key);
      captured = true;
    }
  });

  // Tokens must not linger in the address bar (referrer headers, history, logs).
  if (captured) {
    window.history.replaceState(window.history.state, '', url);
  }
}

// Runs on import, before any API client fires a request.
captureSessionFromUrl();

export const getAccessToken = (): string => read('accessToken');
export const getRefreshToken = (): string => read('refreshToken');

const authApi = new AuthApi(
  new Configuration({
    basePath: ARS_API_BASE_URL,
    headers: {
      'grpc-metadata-agent': 'triplabo'
    }
  })
);

let refreshingPromise: Promise<string | undefined> | null = null;

// Refer ars-neo-frontend
export function refreshAccessToken(): Promise<string | undefined> {
  if (refreshingPromise) {
    return refreshingPromise;
  }
  const refreshToken = getRefreshToken();
  if (!refreshToken || !ARS_API_BASE_URL) {
    return Promise.resolve(undefined);
  }
  refreshingPromise = authApi
    .refreshToken({ body: { refreshToken } })
    .then(res => {
      if (res.accessToken) {
        write('accessToken', res.accessToken);
      }
      if (res.refreshToken) {
        write('refreshToken', res.refreshToken);
      }
      return res.accessToken;
    })
    .catch(() => undefined)
    .finally(() => {
      refreshingPromise = null;
    });
  return refreshingPromise;
}
